import { Alert, Box, Button, Link, Stack, Typography } from '@mui/material';
import React from 'react';
import { Link as RouterLink } from 'react-router-dom';
import {
  ACTIVATION_SECTIONS,
  ALL_FIELDS,
  OFFER_FORM_SECTIONS,
  SETUP_SECTIONS,
} from '../../domain/setup/sections';
import { hasRegistryPermission } from '../../session/RegistrySession';
import { SectionCard } from '../../ui/primitives';
import {
  ActiveFundAlerts,
  BankVerificationButton,
  OfferStatusAlert,
  ReviewRows,
} from './LiveSetupParts';
import LiveSetupFieldStep from './LiveSetupFieldStep';
import type { LiveSetupStepProps as Props } from './liveSetupStepProps';

const sectionForKey = (key: string) =>
  SETUP_SECTIONS.find((item) =>
    item.blocks.some((block) => block.fields.some((field) => field.key === key)),
  );

/** Lists the field errors that belong to this step's part of the setup (register or offer). */
function ErrorLinks({
  errors,
  sections,
  navigateStep,
}: {
  errors: Record<string, string>;
  sections: string[];
  navigateStep: (id: string) => void;
}) {
  const entries = Object.entries(errors).filter(([key]) => {
    const section = sectionForKey(key);
    return !section || sections.includes(section.id);
  });
  if (!entries.length) return null;
  return (
    <Alert severity="error" sx={{ mb: 2 }}>
      <Typography variant="subtitle2">Sections to fix</Typography>
      {entries.map(([key, message]) => {
        const section = sectionForKey(key);
        const label = ALL_FIELDS.find((field) => field.key === key)?.label ?? key;
        return (
          <Box key={key}>
            {section ? (
              <Link component="button" type="button" onClick={() => navigateStep(section.id)}>
                {section.title}: {label}
              </Link>
            ) : (
              label
            )}
            : {message}
          </Box>
        );
      })}
    </Alert>
  );
}

function ActivationStep({
  setup,
  setupId,
  busy,
  session,
  action,
  fieldErrors,
  navigateStep,
}: Props) {
  return (
    <SectionCard title="Activate the register">
      <Typography sx={{ mb: 2 }}>
        Creates the fund, unit class and opening price together.
      </Typography>
      <ActiveFundAlerts fund={setup.fund} />
      {setup.fund && (
        <Stack direction="row" spacing={1} sx={{ mb: 2, flexWrap: 'wrap', rowGap: 1 }}>
          <BankVerificationButton
            fund={setup.fund}
            disabled={busy || !hasRegistryPermission(session, 'RegistrySetup')}
            onRequest={() => action(() => session.api.requestBankVerification(setupId))}
          />
          {!setup.fund.bankVerified && (
            <Button variant="outlined" component={RouterLink} to="/manager/approvals">
              Open approvals
            </Button>
          )}
          <Button
            variant="outlined"
            component={RouterLink}
            to={`/manager/funds/${setup.fund.externalId}`}
          >
            View fund and prices
          </Button>
        </Stack>
      )}
      <ErrorLinks errors={fieldErrors} sections={ACTIVATION_SECTIONS} navigateStep={navigateStep} />
    </SectionCard>
  );
}

function ReviewStep({ setup, values, fieldErrors, navigateStep }: Props) {
  return (
    <SectionCard title="Review and request publication">
      <Typography sx={{ mb: 2 }}>
        A second person with Registry Approval must approve. Approval opens the fund opportunity to
        applications.
      </Typography>
      {setup.fund && !setup.fund.bankVerified && (
        <>
          <Alert severity="warning" sx={{ mb: 2 }}>
            Bank details need second-person verification.
          </Alert>
          <Button variant="contained" sx={{ mb: 2 }} onClick={() => navigateStep('activate')}>
            Open activation
          </Button>
        </>
      )}
      <ErrorLinks errors={fieldErrors} sections={OFFER_FORM_SECTIONS} navigateStep={navigateStep} />
      <ReviewRows values={values} documents={setup.documents} />
      <OfferStatusAlert offer={setup.offer} />
    </SectionCard>
  );
}

/** Review & publish's footer: send the offer for approval, or show that it is waiting. */
function PublishAction({
  setup,
  busy,
  publish,
}: {
  setup: Props['setup'];
  busy: boolean;
  publish: () => void;
}) {
  if (setup.offer?.status === 'PUBLISHED') return null;
  const pending = setup.offer?.status === 'PENDING_APPROVAL';
  const ready = setup.status === 'ACTIVE' && Boolean(setup.fund?.bankVerified);
  return (
    <Stack direction="row" spacing={1}>
      <Button variant="outlined" component={RouterLink} to="/manager/approvals">
        Open approvals
      </Button>
      <Button variant="contained" disabled={busy || pending || !ready} onClick={publish}>
        {pending ? 'Waiting for publish approval' : 'Send for approval'}
      </Button>
    </Stack>
  );
}

function StepAction({
  section,
  setup,
  setupId,
  busy,
  session,
  action,
  attempt,
  next,
  advance,
}: Props & { next?: { id: string }; advance: () => void }) {
  const canEdit = hasRegistryPermission(session, 'RegistrySetup');
  if (section.id === 'activate' && setup.status === 'DRAFT') {
    return (
      <Button
        variant="contained"
        disabled={busy || !canEdit}
        onClick={() => {
          attempt('activate');
          action(() => session.api.activateSetup(setupId));
        }}
      >
        Activate register
      </Button>
    );
  }
  if (section.id === 'review') {
    return (
      <PublishAction
        setup={setup}
        busy={busy}
        publish={() => {
          attempt('publish');
          action(() => session.api.requestOfferPublish(setupId));
        }}
      />
    );
  }
  return (
    <Button variant="contained" disabled={!next || busy} onClick={advance}>
      {section.id === 'activate' ? 'Set up the offer' : 'Next'}
    </Button>
  );
}

export default function LiveSetupStep(props: Props) {
  const { section, setupId, setup, readOnly, session, action, navigateStep } = props;
  const index = SETUP_SECTIONS.findIndex((item) => item.id === section.id);
  const next = SETUP_SECTIONS[index + 1];
  const previous = SETUP_SECTIONS[index - 1];
  let body: React.ReactNode;
  if (section.id === 'activate') body = <ActivationStep {...props} />;
  else if (section.id === 'review') body = <ReviewStep {...props} />;
  else body = <LiveSetupFieldStep {...props} />;
  const advance = () =>
    action(async () => {
      const saved = readOnly ? setup : await session.api.updateSetup(setupId, {}, section.id);
      if (next) navigateStep(next.id);
      return saved;
    });
  return (
    <>
      <Typography variant="h5" component="h2" sx={{ mb: 1 }}>
        {section.title}
      </Typography>
      <Typography color="text.secondary" sx={{ mb: 2 }}>
        {section.summary}
      </Typography>
      {body}
      <Stack direction="row" justifyContent="space-between" sx={{ mt: 2 }}>
        <Button disabled={!previous} onClick={() => previous && navigateStep(previous.id)}>
          Back
        </Button>
        <StepAction {...props} next={next} advance={advance} />
      </Stack>
    </>
  );
}
